import { BranchCode } from "api/risk";
import { GenericMessage } from "components/feedback/Message";
import { GenericLoading } from "components/ui/Loader";
import { CommercialIcon } from "components/icons/customIcons";
import { CardsSection } from "components/layout/Section/CardsSection";
import { FC, useEffect, useMemo } from "react";
import { RealEstateOwnerSection } from "./components/tenant/RealEstateOwnerSection";
import { TenantRealEstateCard } from "./components/tenant/TenantRealEstateCard";
import { TenantManager } from "./components/tenant/TenantManager";
import { useTenantRegistrationPage } from "./hooks/useTenantRegistrationPage";
import { ParcTenancyPage } from "./ParcTenancyPage";
import { useAppDispatch } from "app/hooks";
import { ReferentialKey } from "api/referential";
import { useStepNavigation } from "features/subscription-process/navigation/Steps/useStepNavigation";
import { StepPageWrapper } from "features/subscription-process/navigation/Steps/StepPageWrapper";
import { getMultipleReferentialsByKeys } from "features/referentials/slices/getMultipleReferentialsByKeysThunk";

interface TenantRegistrationPageProps {
  branchCode: BranchCode
}

export const TenantRegistrationPage: FC<TenantRegistrationPageProps> = ({ branchCode }) => {
  // Tunnel parc : le risque n'a pas de bien unique, l'étape se fait bien par
  // bien — isParc vient du hook, qui s'en sert aussi pour ne pas appeler
  // l'endpoint locataires mono-bien (invalide pour un parc).
  const { stepPageConfig, realEstate, contractStartDate, t, tenants, loadTenants, riskId, isParc, isParcLoading } = useTenantRegistrationPage(branchCode)

  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(getMultipleReferentialsByKeys([
      ReferentialKey.IncomeType,
      ReferentialKey.EmploymentStatus,
    ]))
  }, [dispatch])

  const owners = useMemo(() => {
    return realEstate?.owners ?? []
  }, [realEstate])

  const { stepConfig, goToNext, goToPrevious } = useStepNavigation(stepPageConfig)

  if (isParcLoading) {
    return <GenericLoading isPageCentered loading />
  }
  if (isParc && riskId) {
    return <ParcTenancyPage riskId={riskId} />
  }
  if (!realEstate) {
    return <GenericLoading isPageCentered loading />
  }

  return <div className="max-w-screen-2xl p-4 md:p-8 mt-8 mx-auto">
    <StepPageWrapper
      stepConfig={stepConfig}
      onNext={goToNext}
      onPrevious={goToPrevious}
      containerSize='full'
    >
      <CardsSection
        icon={<CommercialIcon className="h-8 w-8 text-primary" />}
        title={t("REAL_ESTATE.SECTIONS.RENTAL_INFO")}
      >
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-2 space-y-4">
            <div className="bg-white rounded-xl border p-4 flex flex-col gap-2">
              <label className="text-sm font-semibold text-primary">
                Date de d'effet du contrat
              </label>
              <div className="px-3 py-2 border border-gray-200 rounded-md">
                {contractStartDate?.toLocaleDateString('fr-FR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            </div>
            <TenantRealEstateCard realEstate={realEstate} />
          </div>
          <div className="col-span-10 p-4 bg-white rounded-xl shadow space-y-4">
            <GenericMessage
              message="Assurez-vous que toutes les informations sont correctes avant la tarification."
              type="info"
              dismissible={false} />
            <RealEstateOwnerSection owners={owners} />
            <TenantManager riskId={riskId} tenants={tenants} loadTenants={loadTenants} />
          </div>
        </div>
      </CardsSection>
    </StepPageWrapper>
  </div>
}
